import { service } from '@ember/service';
import { t } from 'ember-intl';
import { VStack } from 'tio-ui/components/layout';
import Card from 'tio-reimagined/components/card';
import Component from '@glimmer/component';
import FaqAccordion from 'tio-reimagined/components/faq-accordion';
import HeroDashboard from 'tio-employee/components/reimagined/pslf/hero-dashboard';
import PslfDebugPanel from 'tio-employee/components/reimagined/pslf/debug-panel';
import PslfRecommendations from 'tio-employee/components/reimagined/pslf/recommendations';
import {
  PhClipboardText,
  PhUploadSimple,
  PhTrendUp,
  PhBuildings,
  PhCheckCircle,
  PhFileText,
  PhListChecks,
  PhQuestion,
} from 'ember-phosphor-icons';
import SectionHeader from 'tio-reimagined/components/section-header';
import Text from 'tio-reimagined/components/text';
import type { AccordionItem } from 'tio-reimagined/components/faq-accordion';
import type { IntlService } from 'ember-intl';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type { LivePslfData } from 'tio-employee/services/reimagined-data';
import type { PslfDebugOverrides } from 'tio-employee/routes/authenticated/reimagined/pslf/index';
import type PersonModel from 'tio-common/models/person';
import type RecommendationModel from 'tio-common/models/recommendation';

interface PslfIndexModel {
  pslfData: LivePslfData;
  loanSummary: LoanSummary;
  person: PersonModel;
  nsldsFileRequestDate: string | null;
  mostRecentRecommendation?: RecommendationModel;
  debugOverrides?: PslfDebugOverrides;
}

interface PslfIndexSignature {
  Args: {
    model: PslfIndexModel;
  };
}

class PslfIndex extends Component<PslfIndexSignature> {
  @service declare intl: IntlService;

  get isDebugRecs() {
    if (typeof window === 'undefined') return false;
    return new URL(window.location.href).searchParams.get('debug') === 'recs';
  }

  get hasPslfForms() {
    if (this.isDebugRecs) return true;
    return (this.args.model.pslfData?.forms?.length ?? 0) > 0;
  }

  get debugLinkLoans() {
    return this.args.model.debugOverrides?.linkLoans ?? false;
  }

  get faqItems(): AccordionItem[] {
    return [
      {
        question: this.intl.t('pslf.reimagined.faqs.what_is_pslf.question'),
        answer: this.intl.t('pslf.reimagined.faqs.what_is_pslf.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.eligibility_requirements.question'),
        answer: this.intl.t('pslf.reimagined.faqs.eligibility_requirements.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.qualifying_employers.question'),
        answer: this.intl.t('pslf.reimagined.faqs.qualifying_employers.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.how_to_apply.question'),
        answer: this.intl.t('pslf.reimagined.faqs.how_to_apply.answer'),
      },
    ];
  }

  <template>
    <VStack @collapsed={{true}}>
      {{! Hero Section — contextual dashboard for returning users, blank-slate for new }}
      <HeroDashboard
        @pslfData={{@model.pslfData}}
        @loanSummary={{@model.loanSummary}}
        @person={{@model.person}}
        @nsldsFileRequestDate={{@model.nsldsFileRequestDate}}
        @mostRecentRecommendation={{@model.mostRecentRecommendation}}
      />

      {{! Recommendations Section - only when user has submitted PSLF forms }}
      {{#if this.hasPslfForms}}
        <PslfRecommendations
          @loanSummary={{@model.loanSummary}}
          @person={{@model.person}}
          @recommendation={{@model.mostRecentRecommendation}}
          @debug={{this.isDebugRecs}}
          @debugLinkLoans={{this.debugLinkLoans}}
        />
      {{/if}}

      {{! Simple Steps Section }}
      <Card @padding="lg" @roundedXl={{true}}>
        <SectionHeader
          @icon={{PhListChecks}}
          @title={{t "pslf.reimagined.steps.heading"}}
          @iconClass="text-violet-600"
          @level={{2}}
        />
        <div class="space-y-6">
          <div class="flex items-start gap-4">
            <div
              class="shrink-0 w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center"
            >
              <PhClipboardText @size={{20}} class="text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <Text @weight="semibold" class="mb-1">
                {{t "pslf.reimagined.steps.submit_form.title"}}
              </Text>
              <Text @variant="muted">
                {{t "pslf.reimagined.steps.submit_form.description"}}
              </Text>
            </div>
          </div>
          <div class="flex items-start gap-4">
            <div
              class="shrink-0 w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center"
            >
              <PhUploadSimple @size={{20}} class="text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <Text @weight="semibold" class="mb-1">
                {{t "pslf.reimagined.steps.upload_data.title"}}
              </Text>
              <Text @variant="muted">
                {{t "pslf.reimagined.steps.upload_data.description"}}
              </Text>
            </div>
          </div>
          <div class="flex items-start gap-4">
            <div
              class="shrink-0 w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center"
            >
              <PhTrendUp @size={{20}} class="text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <Text @weight="semibold" class="mb-1">
                {{t "pslf.reimagined.steps.track_progress.title"}}
              </Text>
              <Text @variant="muted">
                {{t "pslf.reimagined.steps.track_progress.description"}}
              </Text>
            </div>
          </div>
        </div>
      </Card>

      {{! Eligibility Section }}
      <Card @padding="lg" @roundedXl={{true}}>
        <SectionHeader
          @icon={{PhCheckCircle}}
          @title={{t "pslf.reimagined.eligibility.heading"}}
          @iconClass="text-teal-600"
          @level={{2}}
        />
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="text-center">
            <div
              class="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mx-auto mb-3"
            >
              <PhBuildings @size={{24}} class="text-blue-600 dark:text-blue-400" />
            </div>
            <Text @weight="semibold" class="mb-1">
              {{t "pslf.reimagined.eligibility.qualifying_employer.title"}}
            </Text>
            <Text @variant="muted" @size="sm">
              {{t "pslf.reimagined.eligibility.qualifying_employer.description"}}
            </Text>
          </div>
          <div class="text-center">
            <div
              class="w-12 h-12 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center mx-auto mb-3"
            >
              <PhCheckCircle @size={{24}} class="text-teal-600 dark:text-teal-400" />
            </div>
            <Text @weight="semibold" class="mb-1">
              {{t "pslf.reimagined.eligibility.payments.title"}}
            </Text>
            <Text @variant="muted" @size="sm">
              {{t "pslf.reimagined.eligibility.payments.description"}}
            </Text>
          </div>
          <div class="text-center">
            <div
              class="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center mx-auto mb-3"
            >
              <PhFileText @size={{24}} class="text-orange-600 dark:text-orange-400" />
            </div>
            <Text @weight="semibold" class="mb-1">
              {{t "pslf.reimagined.eligibility.direct_loans.title"}}
            </Text>
            <Text @variant="muted" @size="sm">
              {{t "pslf.reimagined.eligibility.direct_loans.description"}}
            </Text>
          </div>
        </div>
      </Card>

      {{! FAQ Section }}
      <Card @padding="lg" @roundedXl={{true}}>
        <SectionHeader
          @icon={{PhQuestion}}
          @title={{t "pslf.reimagined.faqs.heading"}}
          @iconClass="text-amber-600"
          @level={{2}}
        />
        <FaqAccordion @items={{this.faqItems}} />
      </Card>
    </VStack>

    <PslfDebugPanel />
  </template>
}

export default PslfIndex;
